import { useContext, useEffect, useState } from "react";
import showNotification from "../../../utils/showNotification";
import axios from "axios";
import AuthContext from "../../../contexts/AuthContext";
import { createColumnHelper } from "@tanstack/react-table";
import { Switch } from "@mantine/core";
import { ERROR_HANDLER } from "../../../utils/error-handler";
import { isBase64 } from "../../../utils/isBase64";
import useS3 from "../../useS3";


export default function useLibrary() {
   const { uploadToS3 } = useS3()
   const { authToken } = useContext(AuthContext)
   const [displayForm, setDisplayForm] = useState(false);
   const [initialValuesEdit, setInitialValuesEdit] = useState(null);
   const [deleteModalOpen, setDeleteModalOpen] = useState(false);
   const [selectedContentId, setSelectedContentId] = useState(null);
   const [loading, setLoading] = useState(false)
   const [libraryData, setLibraryData] = useState(null)
   const [isUpdatedData, setIsUpdatedData] = useState(false)

   const columnHelper = createColumnHelper();
   const columns = [
      columnHelper.accessor("title", {
         cell: (info) => {
            const value = info.getValue();
            const upperValue = value?.toUpperCase();
            return upperValue;
         },
         header: "Título",
         enableColumnFilter: true,
         enableSorting: false,
         filter: "text",
         filterFn: "includesString",
      }),
      columnHelper.accessor("type", {
         cell: (info) => {
            const value = info.getValue();
            const upperValue = value?.toUpperCase();
            return upperValue;
         },
         header: "Categoría",
         enableColumnFilter: true,
         enableSorting: false,
         filter: "text",
         filterFn: "includesString",
      }),
      columnHelper.accessor("format", {
         cell: (info) => {
            const value = info.getValue();
            const upperValue = value?.toUpperCase();
            return upperValue;
         },
         header: "Formato",
         enableColumnFilter: true,
         enableSorting: false,
         filter: "text",
         filterFn: "includesString",
      }),
      columnHelper.accessor("is_active", {
         cell: (info) => {
            const contentId = info.row.original._id
            return <Switch
               defaultChecked={info.getValue()}
               onClick={() => toggleContentStatus(contentId, info.getValue())}
            />
         },
         header: "Activo",
         enableColumnFilter: true,
         enableSorting: false,
         filter: "text",
         filterFn: "includesString",
      }),
   ];

   useEffect(() => {
      const getLibrary = async () => {
         try {
            setLoading(true)

            const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_SERVER}admin/academico/library`, {
               headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${authToken}`
               }
            })
            setLibraryData(data)
         } catch (error) {
            console.log(error)
            showNotification({
               color: "red",
               status: "error",
               title: "Operación fallida.",
               message: `No se pudo crear el contenido. Detalle: ${error}`,
               autoClose: 12000,
            });
         } finally {
            setLoading(false)
         }
      }

      getLibrary()
   }, [authToken, isUpdatedData])

   const editContentLibrary = (rowData) => {
      if (rowData) {
         setInitialValuesEdit({
            _id: rowData._id,
            title: rowData.title,
            image: rowData.image,
            description: rowData.description,
            tags: rowData.tags,
            format: rowData.format,
            type: rowData.type,
            url: rowData.url,
            author: rowData.author,
            lang: rowData.lang || "español",
            is_active: rowData.is_active
         });
         setDisplayForm(true);
      }
   };

   const toggleContentStatus = async (contentId, currentStatus) => {
      if (!currentStatus || typeof currentStatus !== 'boolean') return null
      await axios.put(
         `${process.env.REACT_APP_BACKEND_SERVER}admin/academico/library`,
         {
            item: {
               _id: contentId
            },
            field: {
               is_active: currentStatus ? false : true
            }
         }, {
         headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`
         }
      }
      )
   }

   const create = async (values, axiosConfig) => {
      await axios.post(
         `${process.env.REACT_APP_BACKEND_SERVER}admin/academico/library`,
         { data: values },
         axiosConfig
      )
   }
   const edit = async (values, axiosConfig) => {
      const { _id: contentId, ...jsonData } = values

      await axios.put(
         `${process.env.REACT_APP_BACKEND_SERVER}admin/academico/library`,
         {
            item: {
               _id: contentId
            },
            data: jsonData
         },
         axiosConfig
      )
   }

   const deleteContentLibrary = async (contentId = null) => {
      if (deleteModalOpen) {
         try {
            setLoading(true);
            await axios.delete(
               `${process.env.REACT_APP_BACKEND_SERVER}admin/academico/library`,
               {
                  headers: {
                     "Content-Type": "application/json",
                     Authorization: `Bearer ${authToken}`,
                  },
                  data: { _id: selectedContentId._id },
               }
            );
            setIsUpdatedData(!isUpdatedData);
            showNotification({
               color: "green",
               status: "success",
               title: "¡Operación exitosa!",
               message: "El elemento seleccionado ha sido eliminado correctamente.",
               autoClose: 12000,
            });
         } catch (error) {
            console.error(error);
            showNotification({
               color: "red",
               status: "error",
               title: "Operación fallida.",
               message: `No se pudo eliminar el contenido. Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
                  }`,
               autoClose: 12000,
            });
         } finally {
            setDeleteModalOpen(false);
            setLoading(false);
         }
      } else {
         setSelectedContentId(contentId);
         setDeleteModalOpen(true);
      }
   };

   const handleSubmit = async (values) => {
      const config = {
         headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
         },
      };



      try {
         setLoading(true);
         if (values.image && isBase64(values.image)) {
            const response = await uploadToS3({
               image: values.image,
               fileDirectory: `public/images/library`,
               fileName: `${values.title}-content`,
               isRequired: false
            })
            values.image = response
         }
         // Check if it's an edit or a new event
         if (initialValuesEdit) {
            await edit(values, config);
         } else {
            await create(values, config);
         }

         showNotification({
            color: "green",
            status: "success",
            title: "¡Excelente! Operación exitosa.",
            message: `El contenido ${values.name} ha sido creado / editado con éxito.`,
         });

         setIsUpdatedData(!isUpdatedData);
      } catch (error) {
         showNotification({
            color: "red",
            status: "error",
            title: "Operación fallida.",
            message: `No se pudo crear / editar el contenido. Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
               }`,
            autoClose: 12000,
         });
      } finally {
         setLoading(false);
         setDisplayForm(false);
         setInitialValuesEdit(null);
      }
   };

   return {
      displayForm,
      setDisplayForm,
      initialValuesEdit,
      setInitialValuesEdit,
      deleteModalOpen,
      setDeleteModalOpen,
      selectedContentId,
      setSelectedContentId,
      loading,
      setLoading,
      handleSubmit,
      libraryData,
      columns,
      editContentLibrary,
      deleteContentLibrary,
      
   }
}